import React, {useContext, useEffect, useMemo, useState} from "react";
import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import OrderDashboardFilters from "../dashboard/OrderDashboard/Layout/OrderDashboardFilters.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import toast from "react-hot-toast";
import EChart from "./ReportingEChart.jsx";
import {useRenderCurrencyValue, useRenderNumber} from "../../api/utilities/useValueRenderers.js";
import {useTranslation} from "react-i18next";
import moment from "moment-timezone";
import {shippingMethodDisplayName} from "../../utils/shippingMethodPresentation.js";
import {
    ChannelMixColors,
    formatAxisTooltip,
    formatChartCurrency,
    formatChartNumber,
    formatCurrencyItemTooltip,
    KpiCard,
    moneyAmount,
    moneyToMajor,
    OrderStatusSummary,
    Panel,
    projectCurrency,
    ReportingPageDescription,
    snapshotCompletenessAlert,
} from "./ReportingLab.jsx";

const DayMode = {
    Today: "today",
    Yesterday: "yesterday",
};

function rowName(row, fallback = "-") {
    return row?.name || row?.domain || row?.domainName || row?.hostName || row?.countryName || row?.coupon || row?.productName || row?.centreName || fallback;
}

function sumMoney(rows, getter) {
    return rows.reduce((sum, row) => sum + moneyAmount(getter(row)), 0);
}

function firstMoney(...values) {
    return values.find((value) => value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "amount")) ?? null;
}

function readMoneyAmount(value) {
    if (value && typeof value === "object" && Object.prototype.hasOwnProperty.call(value, "amount")) {
        return moneyAmount(value);
    }

    return Number(value ?? 0);
}

function aggregateAmount(rows, nameGetter, valueGetter) {
    const map = new Map();

    rows.forEach((row) => {
        const name = nameGetter(row) || "-";
        map.set(name, (map.get(name) ?? 0) + Number(valueGetter(row) ?? 0));
    });

    return [...map.entries()]
        .map(([name, value]) => ({name, value}))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
}

function metricChange(current, previous) {
    if (!previous) {
        return null;
    }

    return ((current - previous) / Math.abs(previous)) * 100;
}

function comparisonBadge(previousDay, current, t) {
    const change = current === null ? null : metricChange(current, previousDay);
    const displayedChange = change === null ? null : Math.round(change * 10) / 10;
    const value = displayedChange === null
        ? t("reporting_lab.daily.not_comparable", {defaultValue: "nelze porovnat"})
        : `${displayedChange > 0 ? "+" : ""}${formatChartNumber(displayedChange, {maximumFractionDigits: 1})} %`;
    const toneClass = displayedChange === null || displayedChange === 0
        ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        : displayedChange > 0
            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300"
            : "bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300";

    return <span className={`rounded-md px-2 py-1 text-xs font-semibold ${toneClass}`}>
        {value}
    </span>;
}

function comparisonWeekMeta(current, previousWeek, formatter, t) {
    const change = current === null ? null : metricChange(current, previousWeek);
    const label = t("reporting_lab.daily.vs_previous_week", {defaultValue: "vs stejný den minulý týden"});

    if (change === null) {
        return `${label} ${t("reporting_lab.daily.not_comparable", {defaultValue: "nelze porovnat"})}`;
    }

    const displayedChange = Math.round(change * 10) / 10;
    const changeSign = displayedChange > 0 ? "+" : "";
    const value = `${formatter(previousWeek)} (${changeSign}${formatChartNumber(displayedChange, {maximumFractionDigits: 1})} %)`;
    const toneClass = displayedChange === 0
        ? "text-slate-900 dark:text-slate-100"
        : displayedChange > 0
            ? "text-emerald-600 dark:text-emerald-400"
            : "text-rose-600 dark:text-rose-400";

    return <span>{label} <strong className={`whitespace-nowrap font-semibold ${toneClass}`}>{value}</strong></span>;
}

function valueWithComparison(value, current, previousDay, t) {
    return <span className="flex flex-wrap items-center gap-2">
        <span>{value}</span>
        {comparisonBadge(previousDay, current, t)}
    </span>;
}

function moneyBarOption(rows, currency, title, chartTextStyle) {
    const chartRows = rows.slice(0, 10);

    return {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        grid: {left: 12, right: 18, top: 16, bottom: 24, containLabel: true},
        tooltip: {
            trigger: "axis",
            axisPointer: {type: "shadow"},
            formatter: (params) => formatAxisTooltip(params, (value) => formatChartCurrency(value, currency)),
        },
        xAxis: {
            type: "value",
            axisLabel: {fontSize: 12, formatter: formatChartNumber},
        },
        yAxis: {
            type: "category",
            data: [...chartRows].reverse().map((row) => row.name),
            axisLabel: {fontSize: 12, width: 230, overflow: "truncate"},
        },
        series: [
            {
                name: title,
                type: "bar",
                data: [...chartRows].reverse().map((row, index) => ({
                    value: row.value,
                    itemStyle: {color: ChannelMixColors[(chartRows.length - 1 - index) % ChannelMixColors.length]},
                })),
                barMaxWidth: 24,
            },
        ],
    };
}

function DayModeToggle({value, onChange}) {
    const {t} = useTranslation();
    const options = [
        {
            value: DayMode.Today,
            label: t("reporting_lab.daily.today", {defaultValue: "Dnes"}),
        },
        {
            value: DayMode.Yesterday,
            label: t("reporting_lab.daily.yesterday", {defaultValue: "Včera"}),
        },
    ];

    return <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 shadow-sm dark:border-slate-700 dark:bg-slate-800">
        {options.map((option) => (
            <button
                key={option.value}
                type="button"
                onClick={() => onChange(option.value)}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                    value === option.value
                        ? "bg-violet-600 text-white shadow-sm"
                        : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700"
                }`}
            >
                {option.label}
            </button>
        ))}
    </div>;
}

export default function ReportingDailyLab({project}) {
    const {t} = useTranslation();
    const {getAuthorityConfiguration} = useContext(GlobalContext);
    const {et} = useErrorTranslation();
    const renderCurrencyValue = useRenderCurrencyValue();
    const renderNumber = useRenderNumber();
    const [data, setData] = useState(null);
    const [snapshots, setSnapshots] = useState([]);
    const [loading, setLoading] = useState({
        core: true,
        products: true,
        coupons: true,
        payments: true,
        shippings: true,
        countries: true,
    });
    const [dayMode, setDayMode] = useState(DayMode.Today);

    const day = useMemo(() => {
        const date = moment();

        if (dayMode === DayMode.Yesterday) {
            date.subtract(1, "day");
        }

        return date.format("YYYY-MM-DD");
    }, [dayMode]);

    useEffect(() => {
        if (!project?.id) {
            return;
        }

        let active = true;
        setData(null);
        setSnapshots([]);
        setLoading({core: true, products: true, coupons: true, payments: true, shippings: true, countries: true});

        const api = new ErpCalculationApi(getAuthorityConfiguration());
        const dayInput = {day};
        const finishPanel = (panel) => {
            if (active) {
                setLoading((current) => ({...current, [panel]: false}));
            }
        };
        const handleError = (error) => {
            if (active) {
                et(error).then((message) => { if (message) toast.error(message); });
            }
        };
        const loadPanel = (panel, promise) => promise
            .then((value) => {
                if (active) {
                    setData((current) => ({...(current ?? {}), [panel]: value}));
                    finishPanel(panel);
                }
            })
            .catch(handleError);

        api.reportingDaily({projectId: project.id, dayInput})
            .then((result) => {
                if (!active) {
                    return;
                }

                setData((current) => ({
                    ...(current ?? {}),
                    orders: result.current?.orders,
                    channels: result.current?.channels,
                    domains: result.domains,
                    previousDayOrders: result.previousDay?.orders,
                    previousDayChannels: result.previousDay?.channels,
                    previousWeekOrders: result.previousWeek?.orders,
                    previousWeekChannels: result.previousWeek?.channels,
                }));
                setSnapshots([result.currentSnapshot, result.previousDaySnapshot, result.previousWeekSnapshot]);
                finishPanel("core");
            })
            .catch(handleError);

        loadPanel("products", api.dailyProductPerformance({
            projectId: project.id,
            productPerformanceDayInput: {day, limit: 10, page: 1},
        }));
        loadPanel("coupons", api.dailyCouponPerformance({projectId: project.id, dayInput}));
        loadPanel("payments", api.dailyPaymentPerformance({projectId: project.id, dayInput}));
        loadPanel("shippings", api.dailyShippingPerformance({projectId: project.id, dayInput}));
        loadPanel("countries", api.dailyCountryPerformance({projectId: project.id, dayInput}));

        return () => {
            active = false;
        };
    }, [project?.id, day]);

    const dailyCentres = data?.orders?.centres ?? [];
    const centres = dailyCentres;
    const channels = data?.channels?.topChannels ?? [];
    const productsByTurnover = data?.products?.topProductsByTurnoverGross ?? [];
    const coupons = data?.coupons ?? [];
    const payments = data?.payments?.payments ?? [];
    const shippings = data?.shippings?.shippings ?? [];
    const countries = data?.countries?.topCountries ?? [];
    const domains = Array.isArray(data?.domains?.dataByDomain)
        ? data.domains.dataByDomain
        : (Array.isArray(data?.domains) ? data.domains : Object.values(data?.domains?.dataByDomain ?? {}));

    const currency = centres.find((row) => row.turnover?.currency)?.turnover?.currency
        ?? channels.find((row) => row.turnoverGross?.currency)?.turnoverGross?.currency
        ?? productsByTurnover.find((row) => row.turnover?.currency)?.turnover?.currency
        ?? domains.map((row) => firstMoney(row?.turnover, row?.turnoverGross, row?.revenue, row?.spend, row?.value)).find(Boolean)?.currency
        ?? projectCurrency(project);

    const turnover = sumMoney(dailyCentres, (row) => row.turnover);
    const marketingSpend = sumMoney(channels, (row) => row.spendMarketing);
    const pno = turnover > 0 ? (marketingSpend / turnover) * 100 : null;
    const ordersBasis = Number(data?.orders?.ordersReceived ?? 0) + Number(data?.orders?.ordersCompleted ?? 0);
    const averageOrderValue = ordersBasis > 0 ? turnover / ordersBasis : 0;
    const comparisonValues = [
        [data?.previousDayOrders, data?.previousDayChannels],
        [data?.previousWeekOrders, data?.previousWeekChannels],
    ].map(([orders, comparisonChannels]) => {
        const comparisonCentres = orders?.centres ?? [];
        const comparisonTurnover = sumMoney(comparisonCentres, (row) => row.turnover);
        const comparisonOrders = Number(orders?.ordersReceived ?? 0) + Number(orders?.ordersCompleted ?? 0);
        const comparisonMarketing = sumMoney(comparisonChannels?.topChannels ?? [], (row) => row.spendMarketing);

        return {
            orders: comparisonOrders,
            turnover: comparisonTurnover,
            averageOrderValue: comparisonOrders > 0 ? comparisonTurnover / comparisonOrders : 0,
            pno: comparisonTurnover > 0 ? (comparisonMarketing / comparisonTurnover) * 100 : 0,
        };
    });
    const [previousDayValues = {}, previousWeekValues = {}] = comparisonValues;
    const channelRows = channels
        .map((channel) => ({
            name: channel.name ?? "-",
            value: moneyToMajor(channel.turnoverGross),
        }))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
    const productRows = productsByTurnover
        .map((product) => ({name: product.productName ?? "-", value: moneyToMajor(product.turnover)}))
        .filter((row) => row.value > 0)
        .sort((a, b) => b.value - a.value);
    const paymentRows = aggregateAmount(payments, rowName, (row) => row.amount);
    const shippingRows = aggregateAmount(shippings, (row) => shippingMethodDisplayName(row, t), (row) => row.amount);
    const countryRows = aggregateAmount(countries, rowName, (row) => row.amount);
    const domainRows = domains
        .map((domain) => ({
            name: domain?.projectDomain?.hostName ?? rowName(domain),
            turnoverAmount: Object.values(domain?.turnoverGrossGranular ?? {}).reduce((sum, money) => sum + readMoneyAmount(money), 0),
        }))
        .filter((domain) => domain.name !== "-" || domain.turnoverAmount > 0)
        .sort((a, b) => b.turnoverAmount - a.turnoverAmount);
    const couponRows = coupons
        .map((coupon) => ({name: coupon.coupon ?? "-", amount: coupon.amount ?? 0, turnover: moneyAmount(coupon.spends)}))
        .sort((a, b) => b.amount - a.amount);

    const chartTextStyle = {fontSize: 12, color: "#64748b"};
    const chartLegendStyle = {
        textStyle: {fontSize: 14, color: "#64748b"},
        itemWidth: 16,
        itemHeight: 10,
    };

    const channelOption = {
        color: ChannelMixColors,
        textStyle: chartTextStyle,
        tooltip: {
            trigger: "item",
            formatter: (params) => formatCurrencyItemTooltip(params, currency),
        },
        legend: {bottom: 0, left: "center", width: "92%", ...chartLegendStyle},
        series: [
            {
                type: "pie",
                radius: ["44%", "66%"],
                center: ["50%", "44%"],
                avoidLabelOverlap: true,
                label: {
                    show: true,
                    formatter: (params) => `${params.name}: ${formatChartNumber(params.percent)} %`,
                    ...chartTextStyle,
                },
                labelLine: {show: true, length: 12, length2: 8},
                data: channelRows.slice(0, 8),
            },
        ],
    };

    const productOption = moneyBarOption(productRows, currency, t("reporting_lab.series.revenue", {defaultValue: "Obrat"}), chartTextStyle);
    const paymentOption = moneyBarOption(paymentRows, null, t("orders.amount", {defaultValue: "Počet"}), chartTextStyle);
    const shippingOption = moneyBarOption(shippingRows, null, t("orders.amount", {defaultValue: "Počet"}), chartTextStyle);

    return (
        <WideSecureLayout
            title={t("reporting_lab.pages.daily", {defaultValue: "Denní výkon"})}
            actions={<OrderDashboardFilters hideFilters={true} showMarketingFilters={false} showModeSwitch={false}/>}
            alerts={[snapshotCompletenessAlert(snapshots, t)]}
        >
            <div className="grid grid-cols-12 gap-4">
                    <ReportingPageDescription
                        className="col-span-12"
                        title={t("reporting_lab.descriptions.daily.title", {defaultValue: "K čemu slouží denní výkon"})}
                        actions={<DayModeToggle value={dayMode} onChange={setDayMode}/>}
                    >
                        {t("reporting_lab.descriptions.daily.text", {defaultValue: "Rychlý provozní pohled na dnešek nebo včerejšek: objednávky, obrat, marketing, top produkty, platby, dopravu, země, domény a případně střediska."})}
                    </ReportingPageDescription>
                    <div className="col-span-12 md:col-span-3">
                        <KpiCard loading={loading.core}
                            label={t("reporting_lab.daily.orders", {defaultValue: "Objednávky"})}
                            value={valueWithComparison(renderNumber(data?.orders?.ordersTotal ?? 0), ordersBasis, previousDayValues.orders, t)}
                            meta={<OrderStatusSummary
                                received={data?.orders?.ordersReceived ?? 0}
                                completed={data?.orders?.ordersCompleted ?? 0}
                                cancelled={data?.orders?.ordersCancelled ?? 0}
                                renderNumber={renderNumber}
                                t={t}
                            />}
                            helper={comparisonWeekMeta(ordersBasis, previousWeekValues.orders, renderNumber, t)}
                        />
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <KpiCard loading={loading.core} label={t("reporting_lab.daily.turnover", {defaultValue: "Obrat"})} value={valueWithComparison(renderCurrencyValue({amount: turnover, currency}, true), turnover, previousDayValues.turnover, t)} meta={t("reporting_lab.daily.by_centres", {defaultValue: "Celkem bez storen"})} helper={comparisonWeekMeta(turnover, previousWeekValues.turnover, (value) => renderCurrencyValue({amount: value, currency}, true), t)}/>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <KpiCard loading={loading.core} label="PNO" value={valueWithComparison(pno === null ? "-" : `${formatChartNumber(pno)} %`, pno, previousDayValues.pno, t)} meta={t("reporting_lab.daily.marketing_cost", {defaultValue: "Marketing"}) + ` ${renderCurrencyValue({amount: marketingSpend, currency}, true)}`} helper={comparisonWeekMeta(pno, previousWeekValues.pno, (value) => `${formatChartNumber(value, {maximumFractionDigits: 1})} %`, t)}/>
                    </div>
                    <div className="col-span-12 md:col-span-3">
                        <KpiCard loading={loading.core} label="AOV" value={valueWithComparison(renderCurrencyValue({amount: averageOrderValue, currency}, true), averageOrderValue, previousDayValues.averageOrderValue, t)} meta={t("reporting_lab.daily.aov_meta", {defaultValue: "Průměrná hodnota nestornované objednávky"})} helper={comparisonWeekMeta(averageOrderValue, previousWeekValues.averageOrderValue, (value) => renderCurrencyValue({amount: value, currency}, true), t)}/>
                    </div>

                    <Panel loading={loading.core} title={t("reporting_lab.daily.channel_share", {defaultValue: "Podíl kanálů na denním obratu"})} className="col-span-12 xl:col-span-5">
                        <EChart option={channelOption} height={380}/>
                    </Panel>
                    <Panel loading={loading.products} title={t("reporting_lab.daily.top_products", {defaultValue: "Top produkty dne"})} className="col-span-12 xl:col-span-7">
                        <EChart option={productOption} height={380}/>
                    </Panel>

                    <Panel loading={loading.payments} title={t("reporting_lab.daily.payments", {defaultValue: "Platby"})} className="col-span-12 xl:col-span-4">
                        <EChart option={paymentOption} height={340}/>
                    </Panel>
                    <Panel loading={loading.shippings} title={t("reporting_lab.daily.shipping", {defaultValue: "Doprava"})} className="col-span-12 xl:col-span-4">
                        <EChart option={shippingOption} height={340}/>
                    </Panel>
                    <Panel loading={loading.countries} title={t("reporting_lab.daily.countries", {defaultValue: "Země"})} className="col-span-12 xl:col-span-4">
                        <RankingTable rows={countryRows} label={t("reporting_lab.daily.country", {defaultValue: "Země"})} amountLabel={t("orders.amount", {defaultValue: "Počet"})} renderNumber={renderNumber}/>
                    </Panel>

                    <Panel loading={loading.core} title={t("reporting_lab.daily.domains", {defaultValue: "Domény"})} className="col-span-12 xl:col-span-6">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                                <thead>
                                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                    <th className="py-2 pr-4 font-semibold">{t("reporting_lab.daily.domain", {defaultValue: "Doména"})}</th>
                                    <th className="py-2 text-right font-semibold">{t("orders.turnover", {defaultValue: "Obrat"})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {domainRows.map((domain) => (
                                    <tr key={domain.name}>
                                        <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{domain.name}</td>
                                        <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue({amount: domain.turnoverAmount, currency}, true)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    <Panel loading={loading.coupons} title={t("reporting_lab.daily.coupons", {defaultValue: "Kupony dne"})} className="col-span-12 xl:col-span-6">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                                <thead>
                                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                    <th className="py-2 pr-4 font-semibold">{t("reporting_lab.discounts.coupon", {defaultValue: "Kupon"})}</th>
                                    <th className="py-2 pr-4 text-right font-semibold">{t("reporting_lab.discounts.usage", {defaultValue: "Použití"})}</th>
                                    <th className="py-2 text-right font-semibold">{t("reporting_lab.table.revenue", {defaultValue: "Obrat"})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {couponRows.slice(0, 10).map((coupon) => (
                                    <tr key={coupon.name}>
                                        <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{coupon.name}</td>
                                        <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(coupon.amount)}</td>
                                        <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue({amount: coupon.turnover, currency}, true)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>

                    {centres.length > 1 && <Panel loading={loading.core} title={t("reporting_lab.daily.centres", {defaultValue: "Střediska"})} className="col-span-12">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                                <thead>
                                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                                    <th className="py-2 pr-4 font-semibold">{t("orders.centre", {defaultValue: "Středisko"})}</th>
                                    <th className="py-2 pr-4 text-right font-semibold">{t("orders.amount", {defaultValue: "Počet"})}</th>
                                    <th className="py-2 pr-4 text-right font-semibold">{t("orders.turnover", {defaultValue: "Obrat"})}</th>
                                    <th className="py-2 text-right font-semibold">{t("orders.average-order-price", {defaultValue: "Prům. hod. obj."})}</th>
                                </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                                {centres.map((centre) => (
                                    <tr key={centre.centreName}>
                                        <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{centre.centreName}</td>
                                        <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderNumber(centre.orderAmount)}</td>
                                        <td className="py-3 pr-4 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(centre.turnover, true)}</td>
                                        <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderCurrencyValue(centre.averageOrderPrice, true)}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </Panel>}
                </div>
        </WideSecureLayout>
    );
}

function RankingTable({rows, label, amountLabel, renderNumber}) {
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100 text-sm dark:divide-slate-700">
                <thead>
                <tr className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                    <th className="py-2 pr-4 font-semibold">{label}</th>
                    <th className="py-2 text-right font-semibold">{amountLabel}</th>
                </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                {rows.map((row) => (
                    <tr key={row.name}>
                        <td className="py-3 pr-4 font-semibold text-slate-900 dark:text-slate-100">{row.name}</td>
                        <td className="py-3 text-right text-slate-600 dark:text-slate-300">{renderNumber(row.value)}</td>
                    </tr>
                ))}
                </tbody>
            </table>
        </div>
    );
}
